/**
 * Minimal wrapper around Google reCAPTCHA v2 (the checkbox widget), matching
 * what the API's LoginThrottleService verifies server-side (a plain
 * success:true/false check against the siteverify endpoint — see
 * src/api/src/services/LoginThrottleService.js).
 *
 * Loaded lazily (only when the backend actually asks for it via
 * actionRequired: "captchaRequired") rather than on every page load, since
 * reCAPTCHA sets its own tracking cookies.
 */
const SCRIPT_SRC = "https://www.google.com/recaptcha/api.js";

let scriptPromise = null;

function loadScript() {
  if (window.grecaptcha) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

// tracks the widget id per container element, so re-renders don't leak widgets
const widgetIds = new WeakMap();

function waitForGrecaptcha(attempt = 0) {
  if (window.grecaptcha && window.grecaptcha.render) return Promise.resolve();
  if (attempt > 50) return Promise.reject(new Error("reCAPTCHA did not initialize in time"));
  return new Promise((resolve) => setTimeout(resolve, 100)).then(() => waitForGrecaptcha(attempt + 1));
}

/**
 * Renders the reCAPTCHA checkbox into `container` (a DOM element). Safe to
 * call again on the same container — it resets the existing widget instead
 * of rendering a duplicate.
 */
export async function renderRecaptcha(container, siteKey) {
  if (!siteKey) {
    throw new Error("VUE_APP_RECAPTCHA_SITE_KEY is not configured");
  }
  await loadScript();
  await waitForGrecaptcha();

  if (widgetIds.has(container)) {
    window.grecaptcha.reset(widgetIds.get(container));
    return widgetIds.get(container);
  }

  const widgetId = window.grecaptcha.render(container, { sitekey: siteKey });
  widgetIds.set(container, widgetId);
  return widgetId;
}

/** Returns the completed challenge token, or "" if not yet solved. */
export function getRecaptchaToken(container) {
  const widgetId = widgetIds.get(container);
  if (widgetId === undefined || !window.grecaptcha) return "";
  return window.grecaptcha.getResponse(widgetId) || "";
}

/** Resets the widget so the user can retry after a failed login attempt. */
export function resetRecaptcha(container) {
  const widgetId = widgetIds.get(container);
  if (widgetId !== undefined && window.grecaptcha) {
    window.grecaptcha.reset(widgetId);
  }
}
